<script setup>
import { onMounted, ref, watch } from 'vue';
import { useNotificationStore } from '@/stores/NotificationStore.js';

const notificationStore = useNotificationStore();

const props = defineProps({
    leadId: {
        type: [Number, String],
        required: true,
    },
    collapsed: {
        type: Boolean,
        default: true,
    }
});

const events = ref([]);
const loading = ref(false);
const loaded = ref(false);

const loadEvents = async () => {
    if (!props.leadId) {
        events.value = [];
        loaded.value = true;
        return;
    }

    loaded.value = false;
    loading.value = true;

    try {
        const response = await axios.get(route('bo.api.v1.crm.lead.events', props.leadId));

        events.value = response.data.events ?? [];
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('crm:events.title'),
            message: error?.response?.data?.message || 'Failed to load events.',
            dismissAfter: 5000,
        });
    } finally {
        loading.value = false;
        loaded.value = true;
    }
};

watch(
    () => props.leadId,
    (nextLeadId, previousLeadId) => {
        if (nextLeadId && nextLeadId !== previousLeadId) {
            loadEvents();
        }
    }
);

onMounted(() => {
    loadEvents();
});
</script>

<template>
    <card-collapsible :start-collapsed="collapsed">
        <template #header>
            {{ _mft('crm:events.title') }}
        </template>

        <template #body>
            <div class="grid-cols-1 gap-8 md:grid">
                <div class="col-span-1 mt-4">
                    <div
                        v-if="loading && !loaded"
                        class="flex justify-center py-4"
                    >
                        <mf-spinner-medium class="w-12 h-6" />
                    </div>

                    <template v-else>
                        <div
                            v-for="event in events"
                            :key="event.id"
                        >
                            <small>{{ event.created_at_tz }} - {{ event.user_name }}</small>
                            <p>{{ event.content }}</p>
                            <hr class="mt-2 mb-2">
                        </div>

                        <p v-if="events.length === 0">{{ _mft('crm:events.noEventsYet') }}</p>
                    </template>
                </div>
            </div>
        </template>
    </card-collapsible>
</template>
